import { alpha, Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { m } from 'motion/react';
import Image from 'next/image';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { LOYALTY_EASING, tierConfig } from 'constants/loyalty.constants';
import { DEFAULT_CURRENCY } from 'constants/tickets.constants';
import { LoyaltyTierFragment } from 'types/api/account.types';

import { membershipIconLookup } from 'components/@loyalty/utils';

import { calculateProgressBarWidth } from './utils';

interface Props {
  isInitial: boolean;
  currentTier: LoyaltyTierFragment;
  nextTier: LoyaltyTierFragment | undefined;
  currentPoints: number;
  nextPoints: number;
  totalPointsGained: number;
  onTierComplete: () => void;
  showProgress?: boolean;
  sx?: SxProps<Theme>;
}

const LoyaltyProgressContent = ({
  showProgress = true,
  isInitial,
  currentTier,
  nextTier,
  currentPoints,
  nextPoints,
  totalPointsGained,
  sx,
  onTierComplete,
}: Props) => {
  const intl = useIntl();
  const { mixBlendMode, assetsColor, textColor, intlId } =
    tierConfig[currentTier.id];

  const { initialWidth, finalWidth } = calculateProgressBarWidth(
    currentPoints,
    nextPoints,
    nextTier?.minPoints,
  );

  return (
    <Stack
      sx={[
        {
          width: '100%',
          position: 'absolute',
          bottom: 24,
          left: 0,
          px: 3,
          gap: 1.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Image
          src={membershipIconLookup[currentTier.id]}
          width={56}
          height={56}
          alt="Membership icon"
          style={{
            mixBlendMode: mixBlendMode,
            opacity: 0.5,
          }}
          priority
          unoptimized
        />
        {/* Preloads the next tier icon */}
        {nextTier && (
          <Image
            src={membershipIconLookup[nextTier.id]}
            width={1}
            height={1}
            alt="Membership icon"
            priority
            unoptimized
            style={{ visibility: 'hidden' }}
          />
        )}

        <Stack sx={{ flexGrow: 1 }}>
          <Typography
            variant="extraSmall"
            fontWeight="bold"
            sx={{ mb: '2px', opacity: 0.7, color: textColor }}
          >
            {[
              intl.formatMessage({ id: intlId }),
              intl.formatMessage({ id: 'loyalty.title' }),
            ].join(' ')}
          </Typography>
          <Typography
            variant="extraSmall"
            sx={{ opacity: 0.7, color: textColor }}
          >
            <FormattedMessage
              id="loyalty.progress.description.label"
              values={{
                value: totalPointsGained,
              }}
            />
          </Typography>
        </Stack>
      </Box>
      <Box
        sx={{
          display: showProgress ? 'flex' : 'none',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="extraSmall"
          sx={{ whiteSpace: 'nowrap', opacity: 0.7, color: textColor }}
        >
          <FormattedNumber
            value={currentTier.minPoints}
            currency={DEFAULT_CURRENCY}
            style="currency"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        </Typography>
        <Box
          sx={{
            position: 'relative',
            height: 4,
            width: '100%',
            borderRadius: 4,
            backgroundColor: alpha(assetsColor, 0.4),
            overflow: 'hidden',
            mixBlendMode: mixBlendMode,
          }}
        >
          <Box
            key={currentTier.id}
            component={m.div}
            initial={{ width: `${initialWidth}%` }}
            animate={{
              width: `${finalWidth}%`,
            }}
            transition={{
              duration: 0.9,
              ease: LOYALTY_EASING,
              delay: isInitial ? 0.7 : 0,
            }}
            onAnimationComplete={onTierComplete}
            sx={{
              position: 'absolute',
              height: '100%',
              backgroundColor: assetsColor,
            }}
          />
        </Box>
        <Typography
          variant="extraSmall"
          sx={{ whiteSpace: 'nowrap', opacity: 0.7, color: textColor }}
        >
          {!!nextTier ? (
            <FormattedNumber
              value={nextTier.minPoints}
              currency={DEFAULT_CURRENCY}
              style="currency"
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          ) : (
            `${DEFAULT_CURRENCY} ∞`
          )}
        </Typography>
      </Box>
    </Stack>
  );
};

export default LoyaltyProgressContent;
